import axios, { isAxiosError } from "axios";
import type { AxiosResponse } from "axios";

import { getApiErrorMessage, headers, host } from "./shared";
import type { ApiErrorBody } from "../../types/api";
import type { Lesson, LessonInput } from "../../types/lesson";
import type { TeacherInvitation } from "../../types/school";
import type {
  ClassMember,
  ClassMemberBatchResult,
  ClassMemberInput,
  ClassMemberListItem,
  ImportClassResponse,
  ImportedStudentInput,
  JoinCodeStatus,
  SchoolClass,
  SchoolClassInput,
} from "../../types/schoolClass";

type AccessToken = string | null | undefined;

export const createClass = async (
  schoolClass: SchoolClassInput,
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolClass>> => {
  try {
    return await axios.post(
      `${host}/api/schools/${schoolId}/classes`,
      { school_class: schoolClass },
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const importClass = async (
  schoolId: string,
  schoolClass: SchoolClassInput,
  accessToken: AccessToken,
  schoolStudents: ImportedStudentInput[] = [],
): Promise<AxiosResponse<ImportClassResponse>> => {
  try {
    return await axios.post(
      `${host}/api/schools/${schoolId}/classes/import`,
      {
        school_class: schoolClass,
        school_students: schoolStudents,
      },
      headers(accessToken),
    );
  } catch (error) {
    const data = isAxiosError<ApiErrorBody>(error)
      ? error.response?.data
      : undefined;
    const errorMessage = data?.error || data?.errors?.join(", ");
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const putClass = async (
  schoolClass: SchoolClassInput,
  schoolId: string,
  classId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolClass>> => {
  try {
    return await axios.put(
      `${host}/api/schools/${schoolId}/classes/${classId}`,
      { school_class: schoolClass },
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const getClasses = async (
  schoolId: string,
  accessToken: AccessToken,
  filterParams: Record<string, string | boolean> = {},
): Promise<AxiosResponse<SchoolClass[]>> => {
  const hasFilterParams = Object.keys(filterParams).length > 0;
  let filterParamsString = hasFilterParams ? "?" : "";
  if (hasFilterParams) {
    Object.keys(filterParams).forEach((key) => {
      const isLastKey = key === Object.keys(filterParams)[-1];
      filterParamsString += `${key}=${filterParams[key]}${!isLastKey && "&"}`;
    });
    filterParamsString = filterParamsString.slice(0, -1);
  }
  return await axios.get(
    `${host}/api/schools/${schoolId}/classes${filterParamsString}`,
    headers(accessToken),
  );
};

export const getClass = async (
  classId: string,
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolClass>> => {
  return await axios.get(
    `${host}/api/schools/${schoolId}/classes/${classId}`,
    headers(accessToken),
  );
};

export const addClassMember = async (
  userId: string,
  classId: string,
  schoolId: string,
  accessToken: AccessToken,
  type: ClassMemberInput["type"],
): Promise<AxiosResponse<{ class_member: ClassMember }>> => {
  return await axios.post(
    `${host}/api/schools/${schoolId}/classes/${classId}/members`,
    { class_member: { user_id: userId, type } },
    headers(accessToken),
  );
};

export const addClassMembers = async (
  classId: string,
  schoolId: string,
  accessToken: AccessToken,
  classMembers: ClassMemberInput[],
): Promise<AxiosResponse<ClassMemberBatchResult[]>> => {
  return await axios.post(
    `${host}/api/schools/${schoolId}/classes/${classId}/members/batch`,
    { class_members: classMembers },
    headers(accessToken),
  );
};

export const deleteStudentFromClass = async (
  studentId: string,
  classId: string,
  schoolId: string,
  accessToken: AccessToken,
): Promise<void> => {
  let response: AxiosResponse<void>;
  try {
    response = await axios.delete(
      `${host}/api/schools/${schoolId}/classes/${classId}/members/${studentId}`,
      headers(accessToken),
    );
    return response.data;
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const regenerateJoinCode = async (
  schoolId: string,
  classId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<SchoolClass>> => {
  try {
    return await axios.post(
      `${host}/api/schools/${encodeURIComponent(
        schoolId,
      )}/classes/${encodeURIComponent(classId)}/regenerate_join_code`,
      {},
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const getJoinStatus = async (
  joinCode: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<JoinCodeStatus>> => {
  return await axios.get(
    `${host}/api/join/${encodeURIComponent(joinCode)}`,
    headers(accessToken),
  );
};

export const submitJoinCode = async (
  joinCode: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<Record<string, never>>> => {
  return await axios.post(
    `${host}/api/join/${encodeURIComponent(joinCode)}`,
    {},
    headers(accessToken),
  );
};

export const getClassMembers = async (
  classId: string,
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<ClassMemberListItem[]>> => {
  return await axios.get(
    `${host}/api/schools/${schoolId}/classes/${classId}/members`,
    headers(accessToken),
  );
};

export const createLesson = async (
  lesson: LessonInput,
  accessToken: AccessToken,
): Promise<AxiosResponse<Lesson>> => {
  try {
    return await axios.post(
      `${host}/api/lessons`,
      { lesson },
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const getLessons = async (
  classId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<Lesson[]>> => {
  return await axios.get(
    `${host}/api/lessons?school_class_id=${classId}`,
    headers(accessToken),
  );
};

export const getLesson = async (
  lessonId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<Lesson>> => {
  const response = await axios.get(
    `${host}/api/lessons/${lessonId}`,
    headers(accessToken),
  );
  return response;
};

export const updateLesson = async (
  lesson: LessonInput,
  accessToken: AccessToken,
): Promise<AxiosResponse<Lesson>> => {
  return await axios.put(
    `${host}/api/lessons/${lesson.id}`,
    { lesson },
    headers(accessToken),
  );
};

export const copyLesson = async (
  lessonId: string,
  classId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<Lesson>> => {
  return await axios.post(
    `${host}/api/lessons/${lessonId}/copy`,
    { school_class_id: classId },
    headers(accessToken),
  );
};

export const getInvitation = async (
  tokenId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<TeacherInvitation>> => {
  return await axios.get(
    `${host}/api/teacher_invitations/${tokenId}`,
    headers(accessToken),
  );
};

export const acceptInvitation = async (
  tokenId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  return await axios.put(
    `${host}/api/teacher_invitations/${tokenId}/accept`,
    {},
    headers(accessToken),
  );
};

export const deleteClass = async (
  classId: string,
  schoolId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  try {
    return await axios.delete(
      `${host}/api/schools/${schoolId}/classes/${classId}`,
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};

export const deleteLesson = async (
  lessonId: string,
  accessToken: AccessToken,
): Promise<AxiosResponse<void>> => {
  try {
    return await axios.delete(
      `${host}/api/lessons/${lessonId}`,
      headers(accessToken),
    );
  } catch (error) {
    const errorMessage = getApiErrorMessage(error);
    throw errorMessage ? new Error(errorMessage) : error;
  }
};
